import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  RefreshCw,
  Award,
  Palette,
} from "lucide-react";
import api, { ApiResponse } from "../../services/api";
import { toast } from "react-hot-toast";

interface Discount {
  _id: string;
  name: string;
  minPoints: number;
  maxPoints: number;
  discount: number;
  minPointExpiryDays: number;
  maxPointExpiryDays: number;
  color: string;
}

const LevelManagement: React.FC = () => {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  // Form states
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null);
  const [formValues, setFormValues] = useState({
    name: "",
    minPoints: "",
    maxPoints: "",
    discount: "",
    minPointExpiryDays: "",
    maxPointExpiryDays: "",
    color: "#3B82F6",
  });
  const [formLoading, setFormLoading] = useState(false);

  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<ApiResponse<Discount[]>>("/discounts");
      if (response.data && Array.isArray(response.data)) {
        setDiscounts(response.data);
      } else {
        setDiscounts([]);
        setError("Failed to fetch discount levels.");
      }
    } catch (err) {
      setError("Failed to load discount levels. Please try again.");
      setDiscounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formValues.name ||
      !formValues.minPoints ||
      !formValues.maxPoints ||
      !formValues.discount ||
      !formValues.minPointExpiryDays ||
      !formValues.maxPointExpiryDays ||
      !formValues.color
    ) {
      toast.error("Please fill in all fields.");
      return;
    }

    const minPoints = parseInt(formValues.minPoints, 10);
    const maxPoints = parseInt(formValues.maxPoints, 10);
    const discount = parseFloat(formValues.discount);
    const minPointExpiryDays = parseInt(formValues.minPointExpiryDays, 10);
    const maxPointExpiryDays = parseInt(formValues.maxPointExpiryDays, 10);

    if (minPoints >= maxPoints) {
      toast.error("Maximum points must be greater than minimum points.");
      return;
    }

    if (discount <= 0 || discount > 100) {
      toast.error("Discount must be between 0 and 100 percent.");
      return;
    }

    setFormLoading(true);
    setError(null);

    const discountData = {
      ...formValues,
      minPoints,
      maxPoints,
      discount,
      minPointExpiryDays: minPointExpiryDays,
      maxPointExpiryDays: maxPointExpiryDays,
    };

    try {
      if (editingDiscount) {
        await api.put<ApiResponse<Discount>>(
          `/discounts/${editingDiscount._id}`,
          discountData,
        );
        toast.success("Level updated successfully!");
      } else {
        await api.post<ApiResponse<Discount>>("/discounts", discountData);
        toast.success("Level added successfully!");
      }

      fetchDiscounts();
      resetForm();
      setShowModal(false);
    } catch {
      setError("Failed to save level. Please try again.");
      toast.error("Failed to save level.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this level?")) return;

    try {
      setError(null);
      await api.delete(`/discounts/${id}`);
      fetchDiscounts();
      toast.success("Level deleted successfully!");
    } catch {
      setError("Failed to delete level. Please try again.");
      toast.error("Failed to delete level.");
    }
  };

  const handleEdit = (discount: Discount) => {
    setEditingDiscount(discount);
    setFormValues({
      name: discount.name,
      minPoints: discount.minPoints.toString(),
      maxPoints: discount.maxPoints.toString(),
      discount: discount.discount.toString(),
      minPointExpiryDays: discount.minPointExpiryDays.toString(),
      maxPointExpiryDays: discount.maxPointExpiryDays.toString(),
      color: discount.color,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingDiscount(null);
    setFormValues({
      name: "",
      minPoints: "",
      maxPoints: "",
      discount: "",
      minPointExpiryDays: "",
      maxPointExpiryDays: "",
      color: "#3B82F6",
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Filter and pagination logic
  const filteredDiscounts = discounts.filter(
    (discount) =>
      (discount.name &&
        discount.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      discount.minPoints.toString().includes(searchTerm) ||
      discount.maxPoints.toString().includes(searchTerm) ||
      discount.discount.toString().includes(searchTerm),
  );

  const totalPages = Math.ceil(filteredDiscounts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDiscounts = filteredDiscounts.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto p-4 pt-0 overflow-y-auto no-scrollbar lg:p-8 flex flex-col h-screen">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm   py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Levels</h1>
          <button
            onClick={openAddModal}
            className="w-auto self-start sm:self-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition inline-flex items-center gap-2 whitespace-nowrap">
            <Plus size={18} />
            Add New Level
          </button>
        </div>

        {/* ================= SEARCH ================= */}
        <div className=" rounded-lg mb-6 ">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Search Levels
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by name, points, or discount..."
                className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={fetchDiscounts}
              className="h-10 w-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
              title="Refresh Levels"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>
      {/* ================= ERROR ================= */}
      {error && (
        <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-center">
          {error}
        </div>
      )}

      {/* ================= CONTENT ================= */}
      <div className="flex-1">
        {paginatedDiscounts.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No levels found</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try searching with a different term"
                  : "Add your first level to get started"}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {paginatedDiscounts.map((discount) => (
              <div
                key={discount._id}
                className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Level Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full border-2 border-white shadow-md"
                      style={{ backgroundColor: discount.color }}
                    />
                    <h3 className="text-xl mb-0 font-bold text-gray-800">
                      {discount.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <Palette size={14} className="text-gray-400" />
                    <span className="text-xs text-gray-500">
                      {discount.color}
                    </span>
                  </div>
                </div>

                {/* Level Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Award size={16} className="text-yellow-500" />
                    <span className="text-sm text-gray-600">
                      Spending Range: {discount.minPoints.toLocaleString()} -{" "}
                      {discount.maxPoints.toLocaleString()}
                    </span>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-md p-2">
                    <p className="text-green-800 font-semibold text-center mb-0">
                      {discount.discount}% Points
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(discount)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition"
                  >
                    <Edit size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(discount._id)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {filteredDiscounts.length > 0 && totalPages > 1 && (
        <div className="py-4 flex justify-center ">
          <div className="flex gap-2 items-center">
            {/* Previous Button */}
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Prev
            </button>

            {/* Page Numbers */}
            {(() => {
              const pages = [];
              const showEllipsis = totalPages > 7;

              if (!showEllipsis) {
                // Show all pages if 7 or fewer
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === i
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {i}
                    </button>,
                  );
                }
              } else {
                // Smart pagination for many pages
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(totalPages, currentPage + 2);

                // Always show first page
                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      onClick={() => setCurrentPage(1)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === 1
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      1
                    </button>,
                  );

                  if (startPage > 2) {
                    pages.push(
                      <span key="ellipsis1" className="px-2 py-1 text-gray-500">
                        ...
                      </span>,
                    );
                  }
                }

                // Show pages around current page
                for (let i = startPage; i <= endPage; i++) {
                  if (i !== 1 && i !== totalPages) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1 rounded transition ${
                          currentPage === i
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 hover:bg-gray-300"
                        }`}
                      >
                        {i}
                      </button>,
                    );
                  }
                }

                // Always show last page
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <span key="ellipsis2" className="px-2 py-1 text-gray-500">
                        ...
                      </span>,
                    );
                  }

                  pages.push(
                    <button
                      key={totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === totalPages
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {totalPages}
                    </button>,
                  );
                }
              }

              return pages;
            })()}

            {/* Next Button */}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ================= MODAL ================= */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowModal(false)}
          />

          {/* Modal Container */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col mx-auto">
            {/* Modal Header - Fixed */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800 mb-0">
                {editingDiscount ? "Edit Level" : "Add New Level"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              <form id="level-form" onSubmit={handleSubmit} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Level Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formValues.name}
                      onChange={handleChange}
                      placeholder="Enter level name (e.g., Bronze, Silver, Gold)"
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Min spending to retain level *
                      </label>
                      <input
                        type="number"
                        name="minPoints"
                        value={formValues.minPoints}
                        onChange={handleChange}
                        placeholder="1000"
                        required
                        min="0"
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Spending to achieve level *
                      </label>
                      <input
                        type="number"
                        name="maxPoints"
                        value={formValues.maxPoints}
                        onChange={handleChange}
                        placeholder="3000"
                        required
                        min="1"
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Min retaining expiry days *
                      </label>
                      <input
                        type="number"
                        name="minPointExpiryDays"
                        value={formValues.minPointExpiryDays}
                        onChange={handleChange}
                        placeholder="90"
                        required
                        min="1"
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Min achieving expiry days *
                      </label>
                      <input
                        type="number"
                        name="maxPointExpiryDays"
                        value={formValues.maxPointExpiryDays}
                        onChange={handleChange}
                        placeholder="60"
                        required
                        min="1"
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Loyality Percentage *
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="discount"
                        value={formValues.discount}
                        onChange={handleChange}
                        placeholder="10"
                        required
                        min="0"
                        max="100"
                        step="0.1"
                        className="w-full p-2.5 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                        %
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Level Color *
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        name="color"
                        value={formValues.color}
                        onChange={handleChange}
                        required
                        className="w-16 h-10 border border-gray-300 rounded-md cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formValues.color}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            color: e.target.value,
                          }))
                        }
                        placeholder="#3B82F6"
                        className="flex-1 p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm font-mono"
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div className="flex items-start gap-2">
                      <Award size={16} className="text-blue-600 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Level Preview:</p>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full border border-white shadow-sm"
                            style={{
                              backgroundColor: formValues.color || "#3B82F6",
                            }}
                          />
                          <span className="font-medium">
                            {formValues.name || "Level Name"}
                          </span>
                          {formValues.discount && (
                            <span className="text-green-600 font-semibold">
                              ({formValues.discount}% off)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-lg flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="level-form"
                className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                disabled={formLoading}
              >
                {formLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span>
                    {editingDiscount ? "Updating..." : "Adding..."}
                  </span>
                ) : editingDiscount ? (
                  "Update Level"
                ) : (
                  "Add Level"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LevelManagement;
